import type { SaveSyncSettingsBody, SyncSettingsDto, UserSyncSource, OuSyncSource } from '@/api/types';

/** Lo que una tarjeta puede cambiar. Todo lo demás se reenvía tal como vino del servidor. */
export interface CambioDeConfiguracion {
  userSource?: UserSyncSource;
  ouSource?: OuSyncSource;
  enabled?: boolean;
  dryRun?: boolean;
  corporateDomains?: string[];
  /** Va DENTRO de `successFactors`, no en la raíz: es un dato de SuccessFactors. */
  emailSentinelLocalParts?: string[];
}

/**
 * Construye el cuerpo del `PUT /v1/admin/sync-settings` a partir de lo que hay guardado más el
 * cambio de una tarjeta.
 *
 * 🔴 Existe para que haya **un solo sitio** donde se arme este cuerpo. La configuración se reparte
 * en tres pestañas (usuarios, unidades organizacionales, dominios) y las tres guardan por el mismo
 * endpoint, que es un **formulario, no un parche**: lo que no se manda, se pierde. Con el cuerpo
 * armado en cada tarjeta, la de usuarios podía pisar los dominios sin que nada lo delatara.
 *
 * Y ahí está la trampa concreta: `corporateDomains` y `selfRegisterJobFunctions` deciden **quién
 * entra y por qué puerta**. El backend entiende que no lleguen como «no lo cambies» —nunca como
 * «vacíalos»—, así que el peor caso es no cambiar nada; pero reenviarlos explícitamente es lo que
 * hace que la pestaña de dominios y la de usuarios no se peleen por el mismo documento.
 */
export function construirCuerpo(
  data: SyncSettingsDto,
  cambio: CambioDeConfiguracion = {},
): SaveSyncSettingsBody {
  return {
    userSource: cambio.userSource ?? data.userSource,
    ouSource: cambio.ouSource ?? data.ouSource,
    bulkEuropeUsaEnabled: data.bulkEuropeUsaEnabled,
    corporateDomains: cambio.corporateDomains ?? data.corporateDomains,
    successFactors: {
      ...data.successFactors,
      enabled: cambio.enabled ?? data.successFactors.enabled,
      dryRun: cambio.dryRun ?? data.successFactors.dryRun,
      emailSentinelLocalParts:
        cambio.emailSentinelLocalParts ?? data.successFactors.emailSentinelLocalParts,
    },
  };
}
